// src/features/commodities-info/components/CommodityHistoricalChart.tsx
//
// Component Island: 90-day Forge Ultra Signal trend for a single commodity.
// Fetches independently of the parent page (progressive loading + failure
// isolation) by reusing the existing commodities-ranking historical-batch
// endpoint (GET /api/v1/commodities-ranking/historical-batch), so no new
// backend surface is required.
//
// Amber/gold accent (#f59e0b) visually distinguishes the Commodities asset
// class from Stocks (blue) and Crypto (orange/emerald). Slate hexes
// (#0f172a tooltip, #334155 grid) match the suite's institutional palette.

import { useEffect, useState } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    fetchHistoricalBatch,
    type HistoryPoint,
} from '../../commodities-ranking/api';

export const CommodityHistoricalChart = ({ symbol }: { symbol: string }) => {
    const [data, setData] = useState<HistoryPoint[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchHistoricalBatch([symbol], 90)
            .then((res) => {
                if (cancelled) return;
                // Extract this symbol's series from the batch map and ensure
                // oldest-to-newest ordering for a left-to-right time axis.
                const series = res[symbol] ?? [];
                setData(
                    [...series].sort((a, b) => a.date.localeCompare(b.date)),
                );
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load historical signal', err);
                setError('Historical signals unavailable.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 flex flex-col h-[350px] shadow-sm">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    Forge Ultra Signal
                </h2>
                <span className="text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                    90 Days
                </span>
            </div>
            <div className="relative flex-1 w-full">
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center text-amber-500 animate-pulse text-sm">
                        Loading signal trend…
                    </div>
                )}
                {!isLoading && error && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        {error}
                    </div>
                )}
                {!isLoading && !error && data.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        No signal history for this commodity.
                    </div>
                )}

                {!isLoading && !error && data.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={data}
                            margin={{ top: 10, right: 0, left: -20, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient
                                    id="colorCommoditySignal"
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor="#f59e0b"
                                        stopOpacity={0.3}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor="#f59e0b"
                                        stopOpacity={0}
                                    />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.3}
                            />
                            <XAxis
                                dataKey="date"
                                tickFormatter={(val: string) =>
                                    val.substring(5, 10)
                                }
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={30}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                width={48}
                                tickFormatter={(val: number) => val.toFixed(2)}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    borderColor: '#334155',
                                    borderRadius: '8px',
                                }}
                                itemStyle={{
                                    color: '#e2e8f0',
                                    fontSize: '12px',
                                }}
                                labelStyle={{ color: '#94a3b8' }}
                                formatter={(val: number) => [
                                    val.toFixed(3),
                                    'Ultra Signal',
                                ]}
                            />
                            <ReferenceLine
                                y={0}
                                stroke="#64748b"
                                strokeDasharray="3 3"
                            />
                            <Area
                                type="monotone"
                                dataKey="forge_ultra_signal"
                                name="Ultra Signal"
                                stroke="#f59e0b"
                                strokeWidth={2}
                                fill="url(#colorCommoditySignal)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default CommodityHistoricalChart;
